import { FormattedMessage, useIntl } from "react-intl"
import { getPiiProps } from "../../../../../services/tracking"
import { DbgPaths } from "../../../../../utils/Paths"
import { getCategoryName } from "../../ReimbursementCategoryMessages"
import React from "react"
import { Legend, ReimbursementsListSection, ReimbursementSummary } from "../SharedReimbursementElements"
import {
  getCategoryPendingOrCompletedReimbursementsList,
  getCategoryPendingReimbursementsList,
  getCompletedReimbursementsList,
  getReceivedReimbursementsList,
  getReimbursementsRequiringActionList
} from "../ReimbursementListUtils"
import icnExpensesPendingSrc from "./assets/images/icn-expenses-pending.png"
import icnExpensesPending2xSrc from "./assets/images/icn-expenses-pending-2x.png"
import { PlanInfoBadgeContainer } from "../../../../containers/planinfo/PlanInfoBadgeContainer"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Badge,
  Box,
  DownIcon,
  Link,
  Typography,
  useTheme,
  Stack
} from "@carrotfertility/carotene-core"
import { ReimbursementDescriptionGroup, ReimbursementDescriptionList } from "../ReimbursementDescriptionList"
import { PayingForCareCoverageModal } from "../../digital-benefit-guide/PayingForCareModal"
import { ExpenseState } from "../../../../../utils/ExpenseState"
import { getCategoryCoverage } from "../../../../../utils/CategoryCoverage"
import { TextDirection } from "#/types/Locale"
import { useTextDirection } from "../../../../../services/locale/hooks/useTextDirection"
import { useLocation } from "react-router"

// @ts-expect-error TS(7031) FIXME: Binding element 'color' implicitly has an 'any' ty... Remove this comment to see the full error message
const ConditionalBadge = ({ color, variant, overlap, badgeContent, children }) => {
  const textDirection = useTextDirection()

  return badgeContent ? (
    <Badge
      anchorOrigin={{ vertical: "top", horizontal: textDirection === TextDirection.RightToLeft ? "left" : "right" }}
      color={color}
      variant={variant}
      overlap={overlap}
      badgeContent={badgeContent}
    >
      {children}
    </Badge>
  ) : (
    <>{children}</>
  )
}

// @ts-expect-error TS(7031) FIXME: Binding element 'icon' implicitly has an 'any' typ... Remove this comment to see the full error message
const Category = ({ disabled = false, defaultExpanded = false, icon, name, id, summary, details }) => {
  return (
    <Accordion
      disabled={disabled}
      defaultExpanded={defaultExpanded}
      sx={(theme) => ({ paddingBlockEnd: theme.tokens.spacing.md })}
    >
      <AccordionSummary expandIcon={<DownIcon fontSize="large" />} aria-controls={id} id={id}>
        <Box display="flex" flexDirection="row">
          {icon}
        </Box>
        <Box display="flex" flexDirection="column" paddingLeft={(theme) => theme.tokens.spacing.lg}>
          <Typography variant="h5" component="h4" paddingBottom={(theme) => theme.tokens.spacing.md}>
            {name}
          </Typography>
          {summary}
        </Box>
      </AccordionSummary>
      <AccordionDetails>{details}</AccordionDetails>
    </Accordion>
  )
}

interface WaitingReimbursementListProps {
  reimbursements: any[]
  onTransactionClick?: (transaction: any) => void
}

const WaitingReimbursementList = ({ reimbursements, onTransactionClick }: WaitingReimbursementListProps) => {
  const reimbursementsRequiringAction = getReimbursementsRequiringActionList(reimbursements)
  const receivedReimbursements = getReceivedReimbursementsList(reimbursements)
  const intl = useIntl()
  const textDirection = useTextDirection()
  const theme = useTheme()

  return (
    <Category
      disabled={reimbursementsRequiringAction.length + receivedReimbursements.length === 0}
      defaultExpanded={reimbursementsRequiringAction.length > 0}
      icon={
        <Box
          padding={(theme) => theme.tokens.spacing.xxs}
          borderRadius={(theme) => theme.tokens.borderRadius.round}
          height="32px"
          width="32px"
          sx={(theme) => ({ backgroundColor: theme.palette.success.light })}
        >
          <ConditionalBadge
            color="error"
            variant="dot"
            overlap="circular"
            badgeContent={reimbursementsRequiringAction.length}
          >
            <img src={icnExpensesPendingSrc} srcSet={`${icnExpensesPending2xSrc} 2x`} alt="" height={24} width={24} />
          </ConditionalBadge>
        </Box>
      }
      name={<FormattedMessage defaultMessage="Waiting to be processed" />}
      id="processing"
      summary={
        <ReimbursementDescriptionList>
          <ReimbursementDescriptionGroup
            title={<FormattedMessage defaultMessage="# of expenses" />}
            value={reimbursementsRequiringAction.length + receivedReimbursements.length}
          />
          <ReimbursementDescriptionGroup
            title={
              // @ts-expect-error TS(2741) FIXME: Property 'overlap' is missing in type '{ children:... Remove this comment to see the full error message
              <ConditionalBadge color="error" variant="dot" badgeContent={reimbursementsRequiringAction.length}>
                <FormattedMessage defaultMessage="Need your attention" />
              </ConditionalBadge>
            }
            value={reimbursementsRequiringAction.length}
          />
        </ReimbursementDescriptionList>
      }
      details={
        <>
          {reimbursementsRequiringAction.length > 0 && (
            <ReimbursementsListSection
              title={
                <Stack
                  direction={textDirection === TextDirection.RightToLeft ? "row-reverse" : "row"}
                  justifyContent="space-between"
                >
                  <Badge
                    anchorOrigin={{
                      vertical: "top",
                      horizontal: textDirection === TextDirection.RightToLeft ? "left" : "right"
                    }}
                    badgeContent={intl.formatNumber(reimbursementsRequiringAction.length)}
                    color="primary"
                    sx={{
                      "& .MuiBadge-badge.MuiBadge-anchorOriginTopRight": {
                        insetBlockStart: "50%",
                        transform: "scale(1) translate(26px, -70%)"
                      },
                      "& .MuiBadge-badge.MuiBadge-anchorOriginTopLeft": {
                        insetBlockStart: "50%",
                        transform: "scale(1) translate(-26px, -70%)"
                      }
                    }}
                  >
                    <Typography
                      variant="h3"
                      color={(theme) => theme.palette.text.secondary}
                      sx={{ ...theme.typography.chip }}
                    >
                      <FormattedMessage defaultMessage="Needs Attention" />
                    </Typography>
                  </Badge>
                  <Legend />
                </Stack>
              }
              emptyText={intl.formatMessage({ defaultMessage: "No transactions need attention" })}
              items={reimbursementsRequiringAction}
              showAlert={reimbursementsRequiringAction.some(
                (e) =>
                  e.expenseState === ExpenseState.NEEDS_MEMBER_UPLOADS ||
                  e.expenseState === ExpenseState.DENIED_RECONCILIATION_REQUESTED ||
                  e.reconciliationRequestedExpenseId
              )}
              onTransactionClick={onTransactionClick}
            />
          )}
          {receivedReimbursements.length > 0 && (
            <ReimbursementsListSection
              title={intl.formatMessage({ defaultMessage: "Received" })}
              emptyText={intl.formatMessage({ defaultMessage: "No received transactions" })}
              items={receivedReimbursements}
              onTransactionClick={onTransactionClick}
            />
          )}
        </>
      }
    />
  )
}

interface CategoryReimbursementListProps {
  reimbursements: any[]
  subsidy: any
  allSubsidies: any[]
  onTransactionClick?: (transaction: any) => void
}

const CategoryReimbursementList = ({
  reimbursements,
  subsidy,
  allSubsidies,
  onTransactionClick
}: CategoryReimbursementListProps) => {
  const reimbursementsPending = getCategoryPendingReimbursementsList(reimbursements)
  const reimbursementsCompleted = getCompletedReimbursementsList(reimbursements)
  const intl = useIntl()
  const coverage = getCategoryCoverage(allSubsidies)
  const hasQmeCategory = coverage.qme
  const hasMedicationsCategory = coverage.medication
  const categoryName = intl.formatMessage(getCategoryName(subsidy.reimbursementCategoryNameMessageKey, hasQmeCategory))
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const expenseIdFromUrl = parseInt(params.get("expense-id"), 10)
  return (
    <Category
      disabled={reimbursementsPending.length + reimbursementsCompleted.length === 0}
      defaultExpanded={reimbursements.find((r) => r.expenseId === expenseIdFromUrl) !== undefined}
      // TODO: Update is icon to use new Journey Icons.
      // The icon name is currently hardcoded in the database `ReimbursementCategoryName.IconName`
      // Some of the icons are a combination of multiple journey, using the Journey Icons is not enough to represent the category
      // https://carrotfertility.atlassian.net/browse/OP-3043
      icon={
        <img
          src={`/images/${subsidy.reimbursementCategoryIconName || "icn-expenses-other"}.png`}
          srcSet={`/images/${subsidy.reimbursementCategoryIconName || "icn-expenses-other"}-2x.png 2x`}
          alt=""
          height={32}
          width={32}
        />
      }
      name={categoryName}
      id={categoryName}
      summary={
        <>
          <PayingForCareCoverageModal
            subsidyCategory={subsidy}
            hasQmeCategory={hasQmeCategory}
            hasMedicationsCategory={hasMedicationsCategory}
          />
          <PlanInfoBadgeContainer
            subsidy={subsidy}
            allSubsidies={allSubsidies}
            sx={(theme) => ({ paddingBlockStart: theme.tokens.spacing.xs })}
          />
          <Box marginBottom={(theme) => theme.spacing(theme.tokens.spacing.md)} />
          <ReimbursementSummary
            subsidy={subsidy}
            reimbursementsCount={reimbursementsPending.length + reimbursementsCompleted.length}
            showRemainingAnnual={allSubsidies.some(
              (subsidy) => subsidy.yearlyAmounts.max !== subsidy.lifetimeAmounts.max
            )}
            showRemainingLifetime={allSubsidies.some((subsidy) => subsidy.lifetimeAmounts.max !== null)}
          />
        </>
      }
      details={
        <>
          <Legend />
          {reimbursementsPending.length > 0 && (
            <ReimbursementsListSection
              title={intl.formatMessage({ defaultMessage: "Pending" })}
              items={reimbursementsPending}
              onTransactionClick={onTransactionClick}
            />
          )}
          {reimbursementsCompleted.length > 0 && (
            <ReimbursementsListSection
              title={intl.formatMessage({ defaultMessage: "Completed" })}
              items={reimbursementsCompleted}
              onTransactionClick={onTransactionClick}
            />
          )}
        </>
      }
    />
  )
}

interface MultipleCategoriesReimbursementsListsProps {
  reimbursementsList: any[]
  subsidies: any[]
  onTransactionClick?: (transaction: any) => void
}

export const MultipleCategoriesReimbursementsLists = ({
  reimbursementsList,
  subsidies,
  onTransactionClick
}: MultipleCategoriesReimbursementsListsProps) => {
  const hasQmeCategory = getCategoryCoverage(subsidies).qme
  const intl = useIntl()
  return (
    <div {...getPiiProps()}>
      <Typography variant="body1" color="text.secondary" paddingBottom={(theme) => theme.tokens.spacing.md}>
        <FormattedMessage defaultMessage="Your benefit includes separate fund categories for different kinds of eligible care and services. Funds from one category cannot be transferred to another." />
      </Typography>
      <Typography variant="body1" color="text.secondary" paddingBottom={(theme) => theme.tokens.spacing.lg}>
        <FormattedMessage
          defaultMessage="Check your <link>benefit guide</link> to learn what's covered."
          values={{
            link: (linkContent) => (
              <Link href={DbgPaths.PAYING_FOR_CARE} color="inherit">
                {linkContent}
              </Link>
            )
          }}
        />
      </Typography>
      <WaitingReimbursementList reimbursements={reimbursementsList} onTransactionClick={onTransactionClick} />
      {subsidies
        .filter(
          (subsidy) =>
            subsidy.reimbursementCategoryNameMessageKey !== "OTHER" ||
            subsidy.lifetimeAmounts.max !== 0 ||
            getCategoryPendingOrCompletedReimbursementsList(
              reimbursementsList.filter(
                (r) => r.expensePlanCategoryNameMessageKey === subsidy.reimbursementCategoryNameMessageKey
              )
            )
        )
        .map((subsidy) => {
          const reimbursements = reimbursementsList.filter(
            (r) => r.expensePlanCategoryNameMessageKey === subsidy.reimbursementCategoryNameMessageKey
          )
          const categoryNameMessageKey = subsidy.reimbursementCategoryNameMessageKey
          const categoryName = intl.formatMessage(getCategoryName(categoryNameMessageKey, hasQmeCategory))
          return (
            <CategoryReimbursementList
              reimbursements={reimbursements}
              subsidy={subsidy}
              allSubsidies={subsidies}
              onTransactionClick={onTransactionClick}
              key={categoryName}
            />
          )
        })}
    </div>
  )
}
